import React from "react";
import { ReactNode } from "react";
import ObservableState from "../utils/ObservableState";
import * as ui from "../native";
import ObservableComponent from "./ObservableComponent";
import BaseUIProps, { copyBaseUIProps } from "../native/ui/BaseUIProps";
import ObjectObservable from "../utils/ObjectObservable";
import OneTimePassword from "../models/OneTimePassword";
import ResultStatus from "../classes/ResultStatus";
import Result from "../classes/Result";
import Query from "../classes/Query";
import Button from "./Button";
import RecordLogUtils from "../classes/RecordLogUtils";
import ListWrapper from "../utils/ListWrapper";
import ScrollView2 from "./ScrollView2";
import LoginResult from "../classes/LoginResult";
import Developer from "../models/Developer";
import TextView from "./TextView";
import CollectionUtils from "../utils/CollectionUtils";
import LabelWithStringField from "./LabelWithStringField";
import { UsageConstants } from "../rocket/D3ETemplate";
import { BuildContext } from "../classes/BuildContext";

type _BuilderForgotScreenMoveToLogin = () => void;

type _BuilderForgotScreenOnChangePassword = (developer: Developer) => void;

type _CancelOnPressed = (d3eState: BuilderForgotScreenRefs) => void;

type _EnableOnPressed = (d3eState: BuilderForgotScreenRefs) => void;

type _ROTPOnPressed = (d3eState: BuilderForgotScreenRefs) => void;

type _VerifyOtpOnPressed = (d3eState: BuilderForgotScreenRefs) => void;

type _UserEmailOnChanged = (
  text: string,
  d3eState: BuilderForgotScreenRefs
) => void;

export interface BuilderForgotScreenProps extends BaseUIProps {
  key?: string;
  moveToLogin: _BuilderForgotScreenMoveToLogin;
  onChangePassword: _BuilderForgotScreenOnChangePassword;
}
/// To store state data for BuilderForgotScreen
class BuilderForgotScreenRefs {
  public cancel: CancelState = new CancelState();
  public disable: DisableState = new DisableState();
  public enable: EnableState = new EnableState();
  inRefScrollController: ui.ScrollController = new ui.ScrollController();
  inScrollController: ui.ScrollController = new ui.ScrollController();
  public rOTP: ROTPState = new ROTPState();
  setOtpController: ui.TextEditingController = new ui.TextEditingController();
  setOtpFocusNode: ui.FocusNode = new ui.FocusNode();
  public verifyOtp: VerifyOtpState = new VerifyOtpState();
}

interface VerifyOtpWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: BuilderForgotScreenRefs;
  _verifyOTP?: _VerifyOtpOnPressed;
}

class VerifyOtpState extends ObjectObservable {
  private _disable: boolean = false;
  public _hover: boolean = false;
  public _focus: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
  public get focus(): boolean {
    return this._focus;
  }
  public setFocus(val: boolean) {
    let isValChanged: boolean = this._focus !== val;

    if (!isValChanged) {
      return;
    }

    this._focus = val;

    this.fire("focus", this);
  }
}

class _VerifyOtpWithState extends ObservableComponent<VerifyOtpWithStateProps> {
  verifyOtpFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: VerifyOtpWithStateProps) {
    super(props);

    this.initState();
  }
  public get verifyOtp(): VerifyOtpState {
    return this.props.d3eState.verifyOtp;
  }
  public get d3eState(): BuilderForgotScreenRefs {
    return this.props.d3eState;
  }
  public get _verifyOTP(): _VerifyOtpOnPressed {
    return this.props._verifyOTP;
  }
  public initState() {
    super.initState();

    this.updateObservable("verifyOtp", null, this.verifyOtp);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(
      ["verifyOtp", "verifyOtp.", "verifyOtp.focus", "verifyOtp.hover"],
      this.rebuild
    );
  }
  public verifyOtpOnEnter(event): void {
    return this.verifyOtp.setHover(true);
  }
  public verifyOtpOnExit(event): void {
    return this.verifyOtp.setHover(false);
  }
  public verifyOtpOnFocusChange(val): void {
    return this.verifyOtp.setFocus(val);
  }
  public dispose(): void {
    this.verifyOtp.setHover(false);

    this.verifyOtp.setFocus(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      height: 35,
      width: 150,
      child: Button({
        padding: cStyle.tButtonPrimaryButtonPaddingOn,
        decoration: this.verifyOtp.hover
          ? cStyle.tButtonPrimaryButtonDecorationOnHover
          : this.verifyOtp.focus
          ? cStyle.tButtonPrimaryButtonDecorationOnFocus
          : cStyle.tButtonPrimaryButtonDecorationOn,
        disable: this.verifyOtp.disable,
        onPressed: () => {
          this._verifyOTP(this.d3eState);
        },
        onFocusChange: (val) => {
          this.verifyOtpOnFocusChange(val);
        },
        child: TextView({ data: "Verify Code" }),
        onEnter: (event) => {
          this.verifyOtpOnEnter(event);
        },
        onExit: (event) => {
          this.verifyOtpOnExit(event);
        },
      }),
      className: "x8d hc vc",
    });
  }
}
function VerifyOtpWithState(props: VerifyOtpWithStateProps) {
  return React.createElement(_VerifyOtpWithState, props);
}

interface ROTPWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: BuilderForgotScreenRefs;
  _resendOTP?: _ROTPOnPressed;
}

class ROTPState extends ObjectObservable {
  private _disable: boolean = false;
  public _hover: boolean = false;
  public _focus: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
  public get focus(): boolean {
    return this._focus;
  }
  public setFocus(val: boolean) {
    let isValChanged: boolean = this._focus !== val;

    if (!isValChanged) {
      return;
    }

    this._focus = val;

    this.fire("focus", this);
  }
}

class _ROTPWithState extends ObservableComponent<ROTPWithStateProps> {
  rOTPFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: ROTPWithStateProps) {
    super(props);

    this.initState();
  }
  public get rOTP(): ROTPState {
    return this.props.d3eState.rOTP;
  }
  public get d3eState(): BuilderForgotScreenRefs {
    return this.props.d3eState;
  }
  public get _resendOTP(): _ROTPOnPressed {
    return this.props._resendOTP;
  }
  public initState() {
    super.initState();

    this.updateObservable("rOTP", null, this.rOTP);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(["rOTP", "rOTP.", "rOTP.focus", "rOTP.hover"], this.rebuild);
  }
  public rOTPOnEnter(event): void {
    return this.rOTP.setHover(true);
  }
  public rOTPOnExit(event): void {
    return this.rOTP.setHover(false);
  }
  public rOTPOnFocusChange(val): void {
    return this.rOTP.setFocus(val);
  }
  public dispose(): void {
    this.rOTP.setHover(false);

    this.rOTP.setFocus(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      height: 35,
      margin: ui.EdgeInsets.fromLTRB(0.0, 0.0, 7.0, 0.0, new Map()),
      width: 150,
      child: Button({
        padding: cStyle.tButtonSecondaryButtonPaddingOn,
        decoration: this.rOTP.hover
          ? cStyle.tButtonSecondaryButtonDecorationOnHover
          : this.rOTP.focus
          ? cStyle.tButtonSecondaryButtonDecorationOnFocus
          : cStyle.tButtonSecondaryButtonDecorationOn,
        disable: this.rOTP.disable,
        onPressed: () => {
          this._resendOTP(this.d3eState);
        },
        onFocusChange: (val) => {
          this.rOTPOnFocusChange(val);
        },
        child: TextView({ data: "Resend Code" }),
        onEnter: (event) => {
          this.rOTPOnEnter(event);
        },
        onExit: (event) => {
          this.rOTPOnExit(event);
        },
      }),
      className: "x7cc hc vc",
    });
  }
}
function ROTPWithState(props: ROTPWithStateProps) {
  return React.createElement(_ROTPWithState, props);
}

interface DisableWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: BuilderForgotScreenRefs;
}

class DisableState extends ObjectObservable {
  private _disable: boolean = false;
  public _hover: boolean = false;
  public _focus: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
  public get focus(): boolean {
    return this._focus;
  }
  public setFocus(val: boolean) {
    let isValChanged: boolean = this._focus !== val;

    if (!isValChanged) {
      return;
    }

    this._focus = val;

    this.fire("focus", this);
  }
}

class _DisableWithState extends ObservableComponent<DisableWithStateProps> {
  disableFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: DisableWithStateProps) {
    super(props);

    this.initState();
  }
  public get disable(): DisableState {
    return this.props.d3eState.disable;
  }
  public get d3eState(): BuilderForgotScreenRefs {
    return this.props.d3eState;
  }
  public initState() {
    super.initState();

    this.updateObservable("disable", null, this.disable);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(
      ["disable", "disable.", "disable.focus", "disable.hover"],
      this.rebuild
    );
  }
  public disableOnEnter(event): void {
    return this.disable.setHover(true);
  }
  public disableOnExit(event): void {
    return this.disable.setHover(false);
  }
  public disableOnFocusChange(val): void {
    return this.disable.setFocus(val);
  }
  public dispose(): void {
    this.disable.setHover(false);

    this.disable.setFocus(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      child: Button({
        padding: cStyle.tButtonPrimaryButtonPaddingOn,
        decoration: this.disable.hover
          ? cStyle.tButtonPrimaryButtonDecorationOnHover
          : this.disable.focus
          ? cStyle.tButtonPrimaryButtonDecorationOnFocus
          : cStyle.tButtonPrimaryButtonDecorationOn,
        disable: this.disable.disable,
        onFocusChange: (val) => {
          this.disableOnFocusChange(val);
        },
        child: TextView({ data: "Send" }),
        onEnter: (event) => {
          this.disableOnEnter(event);
        },
        onExit: (event) => {
          this.disableOnExit(event);
        },
      }),
      className: "x80d",
    });
  }
}
function DisableWithState(props: DisableWithStateProps) {
  return React.createElement(_DisableWithState, props);
}

interface EnableWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: BuilderForgotScreenRefs;
  _forgotPassword?: _EnableOnPressed;
}

class EnableState extends ObjectObservable {
  private _disable: boolean = false;
  public _hover: boolean = false;
  public _focus: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
  public get focus(): boolean {
    return this._focus;
  }
  public setFocus(val: boolean) {
    let isValChanged: boolean = this._focus !== val;

    if (!isValChanged) {
      return;
    }

    this._focus = val;

    this.fire("focus", this);
  }
}

class _EnableWithState extends ObservableComponent<EnableWithStateProps> {
  enableFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: EnableWithStateProps) {
    super(props);

    this.initState();
  }
  public get enable(): EnableState {
    return this.props.d3eState.enable;
  }
  public get d3eState(): BuilderForgotScreenRefs {
    return this.props.d3eState;
  }
  public get _forgotPassword(): _EnableOnPressed {
    return this.props._forgotPassword;
  }
  public initState() {
    super.initState();

    this.updateObservable("enable", null, this.enable);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(
      ["enable", "enable.", "enable.focus", "enable.hover"],
      this.rebuild
    );
  }
  public enableOnEnter(event): void {
    return this.enable.setHover(true);
  }
  public enableOnExit(event): void {
    return this.enable.setHover(false);
  }
  public enableOnFocusChange(val): void {
    return this.enable.setFocus(val);
  }
  public dispose(): void {
    this.enable.setHover(false);

    this.enable.setFocus(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      child: Button({
        padding: cStyle.tButtonPrimaryButtonPaddingOn,
        decoration: this.enable.hover
          ? cStyle.tButtonPrimaryButtonDecorationOnHover
          : this.enable.focus
          ? cStyle.tButtonPrimaryButtonDecorationOnFocus
          : cStyle.tButtonPrimaryButtonDecorationOn,
        disable: this.enable.disable,
        onPressed: () => {
          this._forgotPassword(this.d3eState);
        },
        onFocusChange: (val) => {
          this.enableOnFocusChange(val);
        },
        child: TextView({ data: "Send" }),
        onEnter: (event) => {
          this.enableOnEnter(event);
        },
        onExit: (event) => {
          this.enableOnExit(event);
        },
      }),
      className: "x7a",
    });
  }
}
function EnableWithState(props: EnableWithStateProps) {
  return React.createElement(_EnableWithState, props);
}

interface CancelWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: BuilderForgotScreenRefs;
  _onCancelHandler?: _CancelOnPressed;
}

class CancelState extends ObjectObservable {
  private _disable: boolean = false;
  public _hover: boolean = false;
  public _focus: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
  public get focus(): boolean {
    return this._focus;
  }
  public setFocus(val: boolean) {
    let isValChanged: boolean = this._focus !== val;

    if (!isValChanged) {
      return;
    }

    this._focus = val;

    this.fire("focus", this);
  }
}

class _CancelWithState extends ObservableComponent<CancelWithStateProps> {
  cancelFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: CancelWithStateProps) {
    super(props);

    this.initState();
  }
  public get cancel(): CancelState {
    return this.props.d3eState.cancel;
  }
  public get d3eState(): BuilderForgotScreenRefs {
    return this.props.d3eState;
  }
  public get _onCancelHandler(): _CancelOnPressed {
    return this.props._onCancelHandler;
  }
  public initState() {
    super.initState();

    this.updateObservable("cancel", null, this.cancel);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(
      ["cancel", "cancel.", "cancel.focus", "cancel.hover"],
      this.rebuild
    );
  }
  public cancelOnEnter(event): void {
    return this.cancel.setHover(true);
  }
  public cancelOnExit(event): void {
    return this.cancel.setHover(false);
  }
  public cancelOnFocusChange(val): void {
    return this.cancel.setFocus(val);
  }
  public dispose(): void {
    this.cancel.setHover(false);

    this.cancel.setFocus(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      child: Button({
        padding: cStyle.tButtonSecondaryButtonPaddingOn,
        decoration: this.cancel.hover
          ? cStyle.tButtonSecondaryButtonDecorationOnHover
          : this.cancel.focus
          ? cStyle.tButtonSecondaryButtonDecorationOnFocus
          : cStyle.tButtonSecondaryButtonDecorationOn,
        disable: this.cancel.disable,
        onPressed: () => {
          this._onCancelHandler(this.d3eState);
        },
        onFocusChange: (val) => {
          this.cancelOnFocusChange(val);
        },
        child: TextView({ data: "Cancel" }),
        onEnter: (event) => {
          this.cancelOnEnter(event);
        },
        onExit: (event) => {
          this.cancelOnExit(event);
        },
      }),
      className: "xa7",
    });
  }
}
function CancelWithState(props: CancelWithStateProps) {
  return React.createElement(_CancelWithState, props);
}

class _BuilderForgotScreenState extends ObservableComponent<BuilderForgotScreenProps> {
  d3eState: BuilderForgotScreenRefs = new BuilderForgotScreenRefs();
  email: string = "";
  developer: Developer = null;
  showOtpPage: boolean = false;
  otpErrorMessages: Array<string> = ListWrapper.widget(
    this,
    "otpErrorMessages"
  );
  showOtpError: boolean = false;
  showError: boolean = false;
  showOtpValidationError: boolean = false;
  userEnteredOTP: string = "";
  token: string = "";
  hitRequest: boolean = false;
  emailInfo: boolean = false;
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: BuilderForgotScreenProps) {
    super(props);

    this.initState();
  }
  public initState() {
    super.initState();

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(
      [
        "email",
        "emailInfo",
        "hitRequest",
        "otpErrorMessages",
        "showError",
        "showOtpError",
        "showOtpPage",
        "showOtpValidationError",
        "userEnteredOTP",
      ],
      this.rebuild
    );
  }
  public setEmail(val: string): void {
    let isValChanged: boolean = this.email !== val;

    if (!isValChanged) {
      return;
    }

    this.email = val;

    this.fire("email", this);
  }
  public setDeveloper(val: Developer): void {
    let isValChanged: boolean = this.developer !== val;

    if (!isValChanged) {
      return;
    }

    this.updateObservable("developer", this.developer, val);

    this.developer = val;

    this.fire("developer", this);
  }
  public setShowOtpPage(val: boolean): void {
    let isValChanged: boolean = this.showOtpPage !== val;

    if (!isValChanged) {
      return;
    }

    this.showOtpPage = val;

    this.fire("showOtpPage", this);
  }
  public setOtpErrorMessages(val: Array<string>): void {
    let isValChanged: boolean = CollectionUtils.isNotEquals(
      this.otpErrorMessages,
      val
    );

    if (!isValChanged) {
      return;
    }

    this.otpErrorMessages.clear();

    this.otpErrorMessages.addAll(val);

    this.fire("otpErrorMessages", this);
  }
  public addToOtpErrorMessages(val: string, index: number = -1): void {
    if (index === -1) {
      if (!this.otpErrorMessages.contains(val)) this.otpErrorMessages.add(val);
    } else {
      this.otpErrorMessages.remove(this.otpErrorMessages.elementAt(index));

      this.otpErrorMessages.add(val);
    }

    this.fire("otpErrorMessages", this, val, true);
  }
  public removeFromOtpErrorMessages(val: string): void {
    this.otpErrorMessages.remove(val);

    this.fire("otpErrorMessages", this, val, false);
  }
  public setShowOtpError(val: boolean): void {
    let isValChanged: boolean = this.showOtpError !== val;

    if (!isValChanged) {
      return;
    }

    this.showOtpError = val;

    this.fire("showOtpError", this);
  }
  public setShowError(val: boolean): void {
    let isValChanged: boolean = this.showError !== val;

    if (!isValChanged) {
      return;
    }

    this.showError = val;

    this.fire("showError", this);
  }
  public setShowOtpValidationError(val: boolean): void {
    let isValChanged: boolean = this.showOtpValidationError !== val;

    if (!isValChanged) {
      return;
    }

    this.showOtpValidationError = val;

    this.fire("showOtpValidationError", this);
  }
  public setUserEnteredOTP(val: string): void {
    let isValChanged: boolean = this.userEnteredOTP !== val;

    if (!isValChanged) {
      return;
    }

    this.userEnteredOTP = val;

    this.fire("userEnteredOTP", this);
  }
  public setToken(val: string): void {
    let isValChanged: boolean = this.token !== val;

    if (!isValChanged) {
      return;
    }

    this.token = val;

    this.fire("token", this);
  }
  public setHitRequest(val: boolean): void {
    let isValChanged: boolean = this.hitRequest !== val;

    if (!isValChanged) {
      return;
    }

    this.hitRequest = val;

    this.fire("hitRequest", this);
  }
  public setEmailInfo(val: boolean): void {
    let isValChanged: boolean = this.emailInfo !== val;

    if (!isValChanged) {
      return;
    }

    this.emailInfo = val;

    this.fire("emailInfo", this);
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return !this.showOtpPage
      ? ScrollView2({
          child: ui.Column({
            mainAxisAlignment: ui.MainAxisAlignment.center,
            children: [
              TextView({
                data: "Forgot Password",
                style: new ui.TextStyle({
                  color: cStyle.tTextViewBoldHeadingColorOn,
                  fontSize: cStyle.tTextViewBoldHeadingFontSizeOn,
                  fontWeight: cStyle.tTextViewBoldHeadingFontWeightOn,
                }),
                className: "x4d hc",
                key: "0",
              }),
              TextView({
                data: "If you have forgotten your Password you can reset it here.",
                textAlign: cStyle.tTextViewParagraphCenterTextAlignOn,
                style: new ui.TextStyle({
                  color: cStyle.tTextViewParagraphCenterColorOn,
                  fontSize: cStyle.tTextViewParagraphCenterFontSizeOn,
                }),
                className: "x67 hc",
                key: "1",
              }),
              ui.Container({ className: "x7b hc vc", key: "2" }),
              ui.Column({
                crossAxisAlignment: ui.CrossAxisAlignment.start,
                children: [
                  ui.Container({
                    width: 333,
                    child: LabelWithStringField({
                      name: "Email Address",
                      value: this.email,
                      help: "Enter email address to reset password",
                      helpUrl: "",
                      placeHolder: "Enter Email Address",
                      autoFocus: true,
                      onTap: () => {
                        this.setStateEmailError(this.d3eState);
                      },
                      onChanged: (text) => {
                        this.userEmailonChanged(text, this.d3eState);

                        this.setStateEmail(text, this.d3eState);
                      },
                      errors: this.emailInfo
                        ? ["Please enter an email address"]
                        : this.showError
                        ? ["You have entered Invalid Email id"]
                        : [],
                    }),
                    key: "0",
                    className: "x04f hc",
                  }),
                ],
                className: "x95 hc",
                key: "3",
              }),
              this.showOtpError
                ? ui.Column({
                    crossAxisAlignment: ui.CrossAxisAlignment.start,
                    children: [
                      this.otpErrorMessages.expand((error) => [
                        TextView({
                          data: error,
                          textAlign: cStyle.tTextViewErrorMessageTextAlignOn,
                          style: new ui.TextStyle({
                            color: cStyle.tTextViewErrorMessageColorOn,
                            fontSize: cStyle.tTextViewErrorMessageFontSizeOn,
                          }),
                          className: "x4b0 hc",
                          key: error?.toString(),
                        }),
                      ]),
                    ],
                    className: "xaa hc",
                  })
                : [],
              ui.Container({ className: "x9f hc vc", key: "5" }),
              ui.Row({
                mainAxisAlignment: ui.MainAxisAlignment.spaceBetween,
                children: [
                  CancelWithState({
                    d3eState: this.d3eState,
                    _onCancelHandler: this.onCancelHandler,
                    key: "0",
                  }),
                  !this.hitRequest
                    ? EnableWithState({
                        d3eState: this.d3eState,
                        _forgotPassword: this.forgotPassword,
                      })
                    : DisableWithState({ d3eState: this.d3eState }),
                ],
                className: "x3a2 hc h",
                key: "6",
              }),
            ],
            className: ui.join(
              this.props.className,
              "BuilderForgotScreen x8c hc h"
            ),
            ...copyBaseUIProps(this.props),
          }),
          scrollDirection: ui.Axis.vertical,
          className: "BuilderForgotScreen x8c hc h",
          controller: this.d3eState.inScrollController,
        })
      : ScrollView2({
          child: ui.Column({
            mainAxisAlignment: ui.MainAxisAlignment.center,
            children: [
              ui.AssetImage({
                path: "images/d3eOnboarding.png",
                width: 65,
                height: 97.5,
                className: "x27 hc vc",
                key: "0",
              }),
              ui.Container({ className: "xff0 hc vc", key: "1" }),
              TextView({
                data:
                  "We have sent an email to " +
                  this.email +
                  " with a One-Time code. Please provide the code below to complete the process.",
                textAlign: cStyle.tTextViewParagraphCenterTextAlignOn,
                style: new ui.TextStyle({
                  color: cStyle.tTextViewParagraphCenterColorOn,
                  fontSize: cStyle.tTextViewParagraphCenterFontSizeOn,
                }),
                className: "x79 hc",
                key: "2",
              }),
              ui.Container({
                child: ui.Column({
                  mainAxisAlignment: ui.MainAxisAlignment.center,
                  children: [
                    TextView({
                      data: "Enter One Time Code",
                      textAlign: cStyle.tTextViewLabelTextTextAlignOn,
                      style: new ui.TextStyle({
                        color: cStyle.tTextViewLabelTextColorOn,
                        fontSize: cStyle.tTextViewLabelTextFontSizeOn,
                        fontWeight: cStyle.tTextViewLabelTextFontWeightOn,
                      }),
                      className: "xc2 hc",
                      key: "0",
                    }),
                    ui.InputField({
                      placeHolder: "Enter The Code",
                      textAlign: ui.TextAlign.center,
                      autofocus: true,
                      value: this.userEnteredOTP,
                      backgroundColor:
                        cStyle.tInputFieldNormalInputfieldBackgroundColorOn,
                      style: cStyle.tInputFieldNormalInputfieldStyleOn,
                      inActiveColor:
                        cStyle.tInputFieldNormalInputfieldInActiveColorOn,
                      placeHolderColor:
                        cStyle.tInputFieldNormalInputfieldPlaceHolderColorOn,
                      cornerRadius:
                        cStyle.tInputFieldNormalInputfieldCornerRadiusOn,
                      cursorColor:
                        cStyle.tInputFieldNormalInputfieldCursorColorOn,
                      controller: this.d3eState.setOtpController,
                      onChanged: (text) => {
                        this.setOTPCode(text, this.d3eState);
                      },
                      onTap: () => {
                        this.setOTPCodeError(this.d3eState);
                      },
                      onFocusChange: (val) => {},
                      focusNode: this.d3eState.setOtpFocusNode,
                      className: "xcc2 hc vc",
                      key: "1",
                    }),
                  ],
                  className: "xb0 hc vc v",
                }),
                className: "xd3 hc vc",
                key: "3",
              }),
              this.showOtpValidationError
                ? TextView({
                    data: "Please enter valid otp !",
                    textAlign: ui.TextAlign.center,
                    style: new ui.TextStyle({
                      color: cStyle.tTextViewErrorMessageColorOn,
                      fontSize: cStyle.tTextViewErrorMessageFontSizeOn,
                    }),
                    className: "x042 hc",
                  })
                : [],
              ui.Row({
                mainAxisAlignment: ui.MainAxisAlignment.spaceBetween,
                children: [
                  ROTPWithState({
                    d3eState: this.d3eState,
                    _resendOTP: this.resendOTP,
                    key: "0",
                  }),
                  VerifyOtpWithState({
                    d3eState: this.d3eState,
                    _verifyOTP: this.verifyOTP,
                    key: "1",
                  }),
                ],
                className: "xef hc h",
                key: "5",
              }),
            ],
            className: ui.join(
              this.props.className,
              "BuilderForgotScreen x8f hc h"
            ),
            ...copyBaseUIProps(this.props),
          }),
          scrollDirection: ui.Axis.vertical,
          className: "BuilderForgotScreen x8f hc h",
          controller: this.d3eState.inRefScrollController,
        });
  }
  public setStateEmail = (
    value: string,
    d3eState: BuilderForgotScreenRefs
  ): void => {
    this.setEmail(value);
  };
  public setStateEmailError = (d3eState: BuilderForgotScreenRefs): void => {
    this.setEmailInfo(false);

    this.setShowError(false);
  };
  public setOTPCode = (
    text: string,
    d3eState: BuilderForgotScreenRefs
  ): void => {
    this.setUserEnteredOTP(text);
  };
  public setOTPCodeError = (d3eState: BuilderForgotScreenRefs): void => {
    this.setShowOtpValidationError(false);
  };
  public forgotPassword = (d3eState: BuilderForgotScreenRefs): void => {
    this.doForgotPassword();
  };
  public doForgotPassword = async (): Promise<void> => {
    this.setHitRequest(true);

    this.setShowOtpError(false);

    this.setShowError(false);

    if (this.email === null || this.email.isEmpty) {
      this.setEmailInfo(true);

      return;
    }

    let otp: OneTimePassword = new OneTimePassword({
      input: this.email.toLowerCase(),
      inputType: "email",
      userType: "Developer",
    });

    let result: Result<OneTimePassword> = await otp.save();

    if (result.status === ResultStatus.Success) {
      this.setToken(otp.token);

      this.setShowOtpPage(true);
    } else {
      this.setOtpErrorMessages(result.errors);

      this.setHitRequest(false);

      this.setShowOtpError(true);
    }
  };
  public verifyOTP = async (
    d3eState: BuilderForgotScreenRefs
  ): Promise<void> => {
    RecordLogUtils.onPressedButtonLog("ForgotPasswordScreen/VerifyCode-Button");

    this.setShowOtpValidationError(false);

    let result: LoginResult = await Query.get().loginWithOTP(
      UsageConstants.QUERY_LOGINWITHOTP_BUILDERFORGOTSCREEN_EVENTHANDLERS_VERIFYOTP_BLOCK,
      { token: this.token, code: this.userEnteredOTP }
    );

    if (result.success) {
      this.setDeveloper(result.userObject as Developer);

      this.developer.setEmail(this.email);

      this.onChangePassword(this.developer);
    } else {
      this.setShowOtpValidationError(true);
    }
  };
  public resendOTP = (d3eState: BuilderForgotScreenRefs): void => {
    RecordLogUtils.onPressedButtonLog("ForgotPasswordScreen/ResendCode-Button");

    this.setShowOtpValidationError(false);

    this.setUserEnteredOTP("");

    this.doForgotPassword();
  };
  public onCancelHandler = (d3eState: BuilderForgotScreenRefs): void => {
    this.moveToLogin();
  };
  public get moveToLogin(): _BuilderForgotScreenMoveToLogin {
    return this.props.moveToLogin;
  }
  public get onChangePassword(): _BuilderForgotScreenOnChangePassword {
    return this.props.onChangePassword;
  }
  public userEmailonChanged = (
    val: string,
    d3eState: BuilderForgotScreenRefs
  ): void => {
    this.setEmail(val);
  };
  public get cancel() {
    return this.d3eState.cancel;
  }
  public get disable() {
    return this.d3eState.disable;
  }
  public get enable() {
    return this.d3eState.enable;
  }
  public get rOTP() {
    return this.d3eState.rOTP;
  }
  public get verifyOtp() {
    return this.d3eState.verifyOtp;
  }
}
export default function BuilderForgotScreen(props: BuilderForgotScreenProps) {
  return React.createElement(_BuilderForgotScreenState, props);
}
